import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Assembly } from './assemblyPose';
import type { AssemblyView } from './assemblyScene';
import { sceneClosed } from './assemblyClashRemote';
import type { ShotCandidate } from './movementShot';
import type { CamFields, Movement } from './assemblyStore';

// EVERY VIEW A SHOT'S RULE WEIGHED, AS PICTURES, under the viewer: one row per shot the sub-step
// has (the movement, the hardware prepared on it, the second hardware), each view labelled the way
// the rule makes it, with how much of the moving part it hides and which one it took (see
// shotCandidates). For judging the rule on what it sees, not on numbers.
//
// TAKEN ON ARRIVAL, once the sub-step is on screen and still (`ready`), once per sub-step: each
// picture is the viewer itself turned to that view and read, so the camera is taken for the time
// it takes and given back as it was (`hold`), row by row.
//
// AND CLICKABLE: a view clicked is played in the viewer in place of its shot's own (see Tried), ringed
// amber; the chosen one keeps its blue border.
export type ShotKind = 'prep' | 'movement' | 'next';
export type CandidateRow = {
  // Which of the sub-step's shots the row is of, the one a click puts its view on (see withTried).
  kind: ShotKind;
  title: string;
  // WHAT THE HIDDEN SHARE IS OF, by name: the hardware the shot is of, or the part that moves.
  measured: string;
  // The moment of the sub-step the views are of.
  at: number;
  // ABSENT FOR A PART PLAYED IN ANOTHER'S SHOT: its title alone.
  compute?: () => Promise<{ list: ShotCandidate[]; picked: number } | null>;
  // A view as the viewer is turned to it: the stored shot, mirrored where the gesture is.
  lookOf: (c: ShotCandidate) => { centre: [number, number, number]; deg: [number, number, number]; zoom: number };
};

// A VIEW CLICKED, PLAYED IN THE VIEWER in place of the shot's own, to be judged in real
// conditions. In memory only: never saved, gone with the sub-step.
export type Tried = { kind: ShotKind; n: number; cam: CamFields };
export function withTried<F extends Pick<Movement, 'prepOn' | 'nextOn'>>(f: F, tried: Tried | null): F {
  if (!tried) return f;
  if (tried.kind === 'movement') return { ...f, ...tried.cam };
  const field = tried.kind === 'prep' ? 'prepOn' : 'nextOn';
  const on = f[field];
  return on ? { ...f, [field]: { ...on, shot: tried.cam } } : f;
}

// How long the pictures may take before they are asked for again, once, and then said to have failed.
const CANDIDATES_MS = 20000;

// WHICH VIEWER, by a number of its own: a sub-step's scene is replaced by the next one's, and the
// pictures asked of the old one are asked again of the new one.
const viewerIds = new WeakMap<object, number>();
let viewersSeen = 0;
const viewerId = (scene: object | null) => {
  if (!scene) return 0;
  if (!viewerIds.has(scene)) viewerIds.set(scene, (viewersSeen += 1));
  return viewerIds.get(scene)!;
};

type Taken = {
  row: CandidateRow;
  views: { label: string[]; hidden: number | null; png: string | null; picked: boolean; cam: CamFields; at: ReturnType<CandidateRow['lookOf']> }[];
};

export function ShotCandidates({ rows, scene, asm, step, hold, ready, log, tried, onTry }: {
  rows: CandidateRow[];
  scene: AssemblyView | null;
  asm: Assembly;
  step: number;
  // Takes the camera, and returns what gives it back as it was.
  hold: () => () => void;
  // The sub-step loaded, placed and not playing: what the pictures may be taken on.
  ready: boolean;
  // Where each attempt is said (the console and the server's log; see AssemblyPhase's sayWait).
  log: (line: string) => void;
  // The view clicked, played in the viewer; null for the shots' own.
  tried: Tried | null;
  // A click: that view in the viewer, at the row's moment (`look`); the one on screen again, the shot's own.
  onTry: (tried: Tried | null, row: CandidateRow, look: ReturnType<CandidateRow['lookOf']>) => void;
}) {
  const { t: tr } = useLanguage();
  const [taken, setTaken] = useState<Taken[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // WHAT THEY WERE LAST TAKEN FOR: the scene and the shots. Asked again whenever either changes,
  // not once per sub-step: asked once, before the viewer was there, the strip spun for minutes on
  // 2-1 and nothing asked again.
  const asked = useRef('');
  const run = useRef(0);
  // The latest of what they are taken with, read when the moment comes rather than when it was set.
  const now = useRef({ rows, scene, asm, hold, log });
  now.current = { rows, scene, asm, hold, log };
  const shots = rows.map((r) => `${r.title}@${r.at}`).join('|');
  // Asked again once after going unanswered (see CANDIDATES_MS).
  const [retry, setRetry] = useState(0);
  const retried = useRef(false);

  useEffect(() => {
    if (!ready) return;
    const why = !now.current.scene ? 'no viewer yet' : !now.current.rows.length ? 'no shot' : '';
    const on = `${viewerId(scene)}|${shots}|${retry}`;
    if (why) { now.current.log(`candidate views: waiting (${why})`); return; }
    if (asked.current === on) return;
    asked.current = on;
    const mine = ++run.current;
    const t0 = performance.now();
    const took = () => `${((performance.now() - t0) / 1000).toFixed(1)} s`;
    now.current.log(`candidate views: taking ${now.current.rows.length} row(s)`);
    setTaken(null);
    setFailed(null);
    // AND NOT FOR EVER: past this, said, and asked again at the next change.
    const late = window.setTimeout(() => {
      if (run.current !== mine) return;
      run.current += 1;
      asked.current = '';
      if (!retried.current) {
        retried.current = true;
        now.current.log(`candidate views: no answer after ${CANDIDATES_MS / 1000} s, asking again`);
        setRetry((r) => r + 1);
        return;
      }
      now.current.log(`candidate views: no answer after ${CANDIDATES_MS / 1000} s, twice`);
      setFailed(`No answer after ${CANDIDATES_MS / 1000} s`);
    }, CANDIDATES_MS);
    void (async () => {
      const out: Taken[] = [];
      try {
        for (const row of now.current.rows) {
          if (!row.compute) { out.push({ row, views: [] }); continue; }
          const got = await row.compute();
          if (run.current !== mine) return;
          const sc = now.current.scene;
          if (!got || !sc) continue;
          // Taken and given back in one go, so the viewer is never left on a candidate between rows.
          const restore = now.current.hold();
          try {
            sc.show(now.current.asm, step, row.at);
            out.push({
              row,
              views: got.list.map((c, i) => {
                const at = row.lookOf(c);
                sc.look(at);
                return { label: c.label, hidden: c.hidden, png: sc.shot(), picked: i === got.picked, cam: c.cam, at };
              }),
            });
          } finally {
            restore();
          }
        }
        if (run.current !== mine) return;
        now.current.log(`candidate views: taken in ${took()}`);
        setTaken(out);
      } catch (e) {
        if (run.current !== mine) return;
        asked.current = '';
        // ASKED OF A SCENE BEING REPLACED: not a failure, the next one is asked when it is there.
        if (sceneClosed(e)) {
          now.current.log(`candidate views: the scene was replaced after ${took()}, waiting for the next`);
          return;
        }
        const said = e instanceof Error ? e.message : String(e);
        now.current.log(`candidate views: failed after ${took()}: ${said}`);
        setFailed(said);
      } finally {
        window.clearTimeout(late);
      }
    })();
  }, [ready, step, scene, shots, retry]);

  if (!rows.length) return null;
  return (
    <div className="mt-3 flex flex-col gap-3">
      {!taken && !failed && ready && <Loader2 className="h-4 w-4 animate-spin self-center text-slate-400" />}
      {failed && <p className="text-center text-xs text-rose-600">{failed}</p>}
      {taken?.map(({ row, views }, n) => {
        // ON SCREEN: the view clicked, else the shot's own, which is the chosen one.
        const shown = tried?.kind === row.kind ? tried.n : views.findIndex((v) => v.picked);
        return (
        <div key={row.title} className="flex flex-col gap-2">
          {/* WHICH SHOT OF THE SUB-STEP, in the order they play. */}
          <p className="text-center text-xs font-semibold text-slate-600">
            {tr('notice.assembly.candidates.shot').replace('{n}', String(n + 1)).replace('{of}', String(taken.length))}: {row.title}
          </p>
          {views.length > 0 && <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {views.map((v, i) => (
              <button
                type="button"
                key={i}
                onClick={() => onTry(i === shown || v.picked ? null : { kind: row.kind, n: i, cam: v.cam }, row, v.at)}
                className={`overflow-hidden rounded-lg bg-white text-left ${v.picked ? 'border-4 border-blue-500' : 'border-2 border-slate-200 hover:border-slate-400'} ${i === shown ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
              >
                {v.png
                  ? <img src={v.png} alt={v.label.join(', ')} className="aspect-video w-full object-cover" />
                  : <div className="aspect-video w-full bg-slate-100" />}
                <span className="flex flex-col items-center gap-0.5 p-1.5 text-center text-[10px] leading-tight text-slate-600">
                  <span className="font-semibold text-slate-700">
                    {i + 1}{v.picked ? ` (${tr('notice.assembly.candidates.chosen')})` : ''}{i === shown ? ` (${tr('notice.assembly.candidates.onScreen')})` : ''}
                  </span>
                  {v.label.map((line) => <span key={line}>{line}</span>)}
                  <span className="tabular-nums">
                    {v.hidden == null
                      ? tr('notice.assembly.candidates.unmeasured')
                      : tr('notice.assembly.candidates.hidden')
                        .replace('{n}', String(Math.round(v.hidden * 100))).replace('{what}', row.measured)}
                  </span>
                </span>
              </button>
            ))}
          </div>}
        </div>
        );
      })}
    </div>
  );
}
